/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/**
 * Číselník názvů chybových hlášení.
 *
 * Spojuje několik nativních rozhraní:
 * [W3C error name](https://heycam.github.io/webidl/#dfn-exception-error-name),
 * [W3C DOMException](https://developer.mozilla.org/en-US/docs/Web/API/DOMException),
 * [W3C Error](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error),
 * [W3C FileException](https://developer.mozilla.org/en-US/docs/Web/API/FileException),
 * [W3C FileError](https://developer.mozilla.org/en-US/docs/Web/API/FileError),
 * [W3C DOMError](https://developer.mozilla.org/en-US/docs/Web/API/DOMError),
 * [Cordova](https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file/index.html#list-of-error-codes-and-meanings),
 * [webOS](https://webossignage-docs.developer.lge.com/apis/scap-api/previous-versions/scap-api-v1.4/error-code),
 * [Tizen](http://www.samsungdforum.com/B2B/Guide/APIReference/SSSP4/tizen961/index.html#::Tizen::WebAPIError).
 *
 * | Error name | W3C Error | W3C DOMException | Cordova | webOS |
 * | --- | --- | --- | --- | --- |
 * | `AbortError` | - | `AbortError` | `ABORT_ERR` | - |
 * | `EncodingError` | - | `EncodingError` | `ENCODING_ERR` | - |
 * | `Error` | `Error` | - | - | - |
 * | `InvalidModificationError` | - | `InvalidModificationError` | `INVALID_MODIFICATION_ERR` | - |
 * | `InvalidStateError` | - | `InvalidStateError` | `INVALID_STATE_ERR` | - |
 * | `MediaError` | - | - | - | - |
 * | `NetworkError` | - | `NetworkError` | - | `NETWORK_ERROR` |
 * | `NoModificationAllowedError` | - | `NoModificationAllowedError` | `NO_MODIFICATION_ALLOWED_ERR` | - |
 * | `NotFoundError` | - | `NotFoundError` | `NOT_FOUND_ERR` | [2] |
 * | `NotReadableError` | - | - | `NOT_READABLE_ERR` | `DEVICE_ERROR`, `IO_ERROR` |
 * | `NotSupportedError` | - | `NotSupportedError` | - | - |
 * | `PathExistsError` | - | - | `PATH_EXISTS_ERR` | [2] |
 * | `QuotaExceededError` | - | `QuotaExceededError` | `QUOTA_EXCEEDED_ERR` | - |
 * | `RangeError` | `RangeError` | - | - | - |
 * | `ReferenceError` | `ReferenceError` | - | - | - |
 * | `SecurityError` | - | `SecurityError` | `SECURITY_ERR` | - |
 * | `SyntaxError` | [1] | `SyntaxError` | `SYNTAX_ERR` | - |
 * | `TimeoutError` | - | `TimeoutError` | - | - |
 * | `TypeError` | `TypeError` | `TypeMismatchError`[3] | `TYPE_MISMATCH_ERR` | `BAD_PARAMETER` |
 *
 * * [1] Záměrně není uvedený [SyntaxError](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/SyntaxError),
 *   který by měl vyhazovat pouze interpret.
 *   [Podrobnější informace lze nalézt u W3C.](https://heycam.github.io/webidl/#idl-DOMException-error-names)
 * * [2] Lze zjistit pomocí `Storage#exists`.
 * * [3] Deprecated. Nahrazuje TypeError.
 *
 * Jsou vynechány některé typy W3C Error: `EvalError`, `SyntaxError` a `URIError`,
 * které by měli být vyvolávány pouze nativními metodami.
 * @enum {string}
 */
const ErrorName = {
  /** The operation was aborted. */
  AbortError: 'AbortError',
  /** The encoding or decoding operation failed. */
  EncodingError: 'EncodingError',
  /** ViewerController can not play content. */
  EnterPlayingError: 'EnterPlayingError',
  /** Common unspecified error. */
  Error: 'Error',
  /**
   * The object can not be modified in this way, because it makes no sense.
   * For example, the app might be trying to move a directory into its own child or
   * moving a file into its parent directory without changing its name.
   */
  InvalidModificationError: 'InvalidModificationError',
  /**
   * The object is in an invalid state.
   * For example, the state that was cached in an interface object has changed
   * since it was last read from disk.
   */
  InvalidStateError: 'InvalidStateError',
  /**
   * The MediaError represents an error which occurred while handling media
   * in an HTML media element based on HTMLMediaElement, such as <audio> or <video>.
   */
  MediaError: 'MediaError',
  MissingMediumError: 'MissingMediumError',
  /** The network connection has failed. */
  NetworkError: 'NetworkError',
  /**
   * The object can not be modified, because it isn't allowed for some reason.
   * For example, the state of the underlying file system prevents any writing to a file or a directory.
   */
  NoModificationAllowedError: 'NoModificationAllowedError',
  /**
   * The object can not be found here.
   * For example, a file did not exist but was being opened.
   * A required file or directory could not be found at the time an operation was processed.
   */
  NotFoundError: 'NotFoundError',
  /**
   * The input/output read operation failed.
   * Typically due to permission problems that occur after a reference to a file has been acquired.
   * For example, the file or directory is concurrently locked by another application.
   */
  NotReadableError: 'NotReadableError',
  /** The operation is not supported. */
  NotSupportedError: 'NotSupportedError',
  /** The file or directory with the same path already exists. */
  PathExistsError: 'PathExistsError',
  /** The operation failed because it would cause the application to exceed its storage quota. */
  QuotaExceededError: 'QuotaExceededError',
  /** The value is not in the set or range of allowed values. */
  RangeError: 'RangeError',
  /** De-referencing an invalid reference. */
  ReferenceError: 'ReferenceError',
  /**
   * The operation is insecure.
   * For example, a required file was unsafe for access within a Web application.
   * Too many calls are being made on resources.
   */
  SecurityError: 'SecurityError',
  /**
   * An error is occurred during the registration of the device on a remote server.
   * See {@link SetupError} for details.
   */
  SetupError: 'SetupError',
  /** An error is occurred when video seek in deterministic playlist doesn't work properly. */
  SeekError: 'SeekError',
  /** An error is occurred when video seek is not supported or disabled. */
  SeekNotSupportedError: 'SeekNotSupportedError',
  /** The string did not match the expected pattern. */
  SyntaxError: 'SyntaxError',
  /** The operation has timed out. */
  TimeoutError: 'TimeoutError',
  /**
   * Variable or parameter is not of a valid type.
   * For example, the app is asking for a directory, when the entry is really a file.
   */
  TypeError: 'TypeError',
  /** An error is occurred when try to play unsupported content. */
  UnsupportedMimeTypeError: 'UnsupportedMimeTypeError'
};

export default ErrorName;
